<template>
  <div
    class="edit-product-container max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8"
  >
    <div class="mb-6 flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-white mb-2">Izmeni oglas</h1>
        <p class="text-gray-400">Ažurirajte podatke o proizvodu</p>
      </div>
      <div>
        <router-link
          to="/my-listings"
          class="px-4 py-2 text-sm rounded-md bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          Nazad na moje oglase
        </router-link>
      </div>
    </div>

    <!-- Loader dok se podaci ne učitaju -->
    <div
      v-if="isLoading && !isDataLoaded"
      class="bg-gray-800 shadow rounded-lg p-6 text-center"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"
      ></div>
      <p class="mt-4 text-gray-300">Učitavanje podataka...</p>
    </div>

    <!-- Poruka o grešci -->
    <div
      v-else-if="error && !isDataLoaded"
      class="bg-gray-800 shadow rounded-lg p-6 text-center"
    >
      <div class="text-red-500 text-lg">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-10 w-10 mx-auto mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p>{{ error }}</p>
      </div>
      <button
        @click="loadProduct($route.params.id as string)"
        class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
      >
        Pokušaj ponovo
      </button>
    </div>

    <!-- Kartica sa formom kada su podaci učitani - DODATO overflow-hidden! -->
    <div
      v-else-if="isDataLoaded"
      class="bg-gray-800 shadow rounded-lg px-4 py-5 sm:p-6 overflow-hidden"
    >
      <ProductEditForm
        :form-data="formData"
        :is-loading="isLoading"
        :categories="categories"
        @add-image="addImage"
        @remove-image="removeImage"
        @set-main-image="setMainImage"
        @rotate-image="rotateImage"
        @update-product="updateProduct"
        @cancel-changes="cancelChanges"
      />
    </div>

    <!-- Toast notifikacija -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import ProductEditForm from '@/components/product/ProductEditForm.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import { useProductEdit } from '@/composables/useProductEdit';

export default defineComponent({
  name: 'EditProductView',
  components: {
    ProductEditForm,
    ToastNotification,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const {
      formData,
      isLoading,
      error,
      categories,
      toast,
      isDataLoaded,
      loadCategories,
      loadProduct,
      addImage,
      removeImage,
      setMainImage,
      rotateImage,
      updateProduct,
      cancelChanges,
      originalProduct,
    } = useProductEdit();

    // Provera da li je korisnik prijavljen
    const isLoggedIn = computed(() => store.state.auth.isLoggedIn);
    const currentUser = computed(() => store.getters['auth/currentUser']);

    onMounted(async () => {
      if (!isLoggedIn.value) {
        // Sačuvaj putanju za redirekciju nakon prijave
        localStorage.setItem('redirectAfterLogin', route.fullPath);
        router.push('/login');
        return;
      }

      // Učitaj kategorije
      await loadCategories();

      // Provera da li je productId prosleđen kroz parametre
      const productId = route.params.id;
      if (productId) {
        await loadProduct(productId as string);

        // Provera da li je trenutni korisnik vlasnik proizvoda
        // Ovo radimo kada se podaci učitaju
        if (isDataLoaded.value && formData.value.id) {
          // Ovde bismo trebali imati proveru da li je korisnik vlasnik proizvoda
          // ali pošto nemamo direktnu informaciju o vlasniku u podacima proizvoda,
          // možemo dodati tu proveru kada implementiramo API za detalje proizvoda
        }
      } else {
        error.value = 'ID proizvoda nije prosleđen';
        toast.value = {
          show: true,
          message: 'ID proizvoda nije prosleđen',
          type: 'error',
        };
      }
    });

    return {
      formData,
      isLoading,
      error,
      categories,
      toast,
      isDataLoaded,
      loadProduct,
      addImage,
      removeImage,
      setMainImage,
      rotateImage,
      updateProduct,
      cancelChanges,
      originalProduct,
    };
  },
});
</script>

<style scoped>
.edit-product-container {
  position: relative;
  width: 100%;
}
</style>
